/* eslint-disable camelcase */
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import Switch from '@material-ui/core/Switch';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';

import { Helper } from '../../../common/helpers/Helper';
import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { UiHelper } from '../../../common/helpers/UiHelper';
import { Controller } from '../../../common/Controller';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { AppConfig } from '../../../AppConfig';
import { AppStyles } from '../../../styles/AppTheme';
import { SearchBar } from '../../../common/components/SearchBar';
import { botJsonFromChatbot, chatbotFromRecord, createEmptyChatbot } from './whatsapp-chatbot/chatbotGraph';
import {
  firstTemplateIssue,
  indexWaTemplatePreviews,
  invalidStepsReason,
  mappedTemplateKeys,
} from './whatsapp-chatbot/chatbotTemplatePreview'

function isChatbotLive(status) {
  return String(status || '').toUpperCase() === 'LIVE'
}

function withChatbotState(list, id, state) {
  return (list || []).map(item => (item && item.id === id ? { ...item, state } : item))
}

function filterRows(masterList, searchText) {
  const rows = masterList || []
  const q = (searchText || '').trim().toLowerCase()
  if (!q) return rows
  return rows.filter(r => String(r.title || '').toLowerCase().includes(q))
}

function recordId(response) {
  return String((response && (response.id || (response.item && response.item.id))) || '').trim()
}

function getEditChatbotFieldDef() {
  return {
    title: 'Edit chatbot',
    formType: 'edit',
    ctaButtonText: 'Save',
    steps: [{
      fields: [
        {
          key: 'title',
          type: 'edit_text',
          label: 'Name',
          v_required: { value: true, err: 'Provide chatbot name' },
          placement: 'left',
        },
      ],
    }],
  }
}

function getAddChatbotFieldDef() {
  return {
    title: 'Add chatbot',
    ctaButtonText: 'Save',
    steps: [{
      fields: [
        {
          key: 'title',
          type: 'edit_text',
          label: 'Name',
          v_required: { value: true, err: 'Provide chatbot name' },
          placement: 'left',
        },
      ],
    }],
  }
}

export class WhatsappChatbots extends BaseEntityPageSlixta {
  constructor(props) {
    super(props)
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageTitle = 'WhatsApp chatbots'
    this.pageKey = 'whatsapp-chatbots'
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.noAdd = true
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: this.pageTitle },
    ]
    this.toFetch = [
      {
        key: 'chatbots',
        ms: this.ms,
        method: 'GET',
        paths: ['wacontents'],
        queryParams: { orgId: this.orgId, iType: 'CHATBOT' },
      },
    ]
    this.apiData = { orgId: this.orgId }
    this.tableHeadersMap = {
      Index: current => current.__index + 1,
      Name: current => (
        <div>
          <span className={css(Styles.line1)}>{current.title || '—'}</span>
        </div>
      ),
      Status: current => {
        const isLive = isChatbotLive(current.state)
        const isToggling = Boolean(this.state.togglingIds && this.state.togglingIds[current.id])
        return (
          <div className={css(Styles.statusCell)} onClick={e => e.stopPropagation()}>
            {isToggling ? (
              <CircularProgress size={20} style={{ margin: '0 8px' }} />
            ) : (
              <Switch
                checked={isLive}
                onChange={e => this.onToggleChatbotState(current, e.target.checked)}
                color='primary'
                size='small'
              />
            )}
          </div>
        )
      },
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    this.headerActions = [
      {
        label: 'Add chatbot',
        onClick: () => this.openModalForAddChatbot(),
      },
      {
        label: 'Refresh',
        tooltip: 'Refresh',
        icon: 'refresh',
        type: 'icon-label',
        onClick: () => this.fetchItems(),
      },
    ]
    this.state = {
      ...this.state,
      masterItems: [],
      searchText: '',
      togglingIds: {},
    }
  }

  onFetchSuccess(results) {
    const result = this.getResult(results, 'chatbots')
    const masterItems = Array.isArray(result) ? result : ((result && result.items) || [])
    this.setState({
      masterItems,
      items: filterRows(masterItems, this.state.searchText),
    })
  }

  putChatbot(id, { title, state }) {
    return ApiHelper.callAwait({
      ms: this.ms,
      method: 'PUT',
      paths: ['wacontents', id],
      queryParams: { orgId: this.orgId },
      jsonBody: {
        orgId: this.orgId,
        title,
        state,
      },
    })
  }

  openModalForAddChatbot = () => {
    this.apiData = { orgId: this.orgId }
    super.openModalForAdd(getAddChatbotFieldDef())
  }

  openModalForEdit(current) {
    this.apiData = {
      orgId: this.orgId,
      id: current && current.id,
      state: current && current.state,
    }
    super.openModalForEdit({
      title: (current && current.title) || '',
    }, getEditChatbotFieldDef())
  }

  openChatbot = (id) => {
    if (!id) return
    this.props.history.push(`${this.baseRoute()}/whatsapp-chatbots/${id}`)
  }

  async liveBlockReason(current) {
    let record = current
    if (!record || !record.botJson) {
      record = await ApiHelper.callAwait({
        ms: this.ms,
        method: 'GET',
        paths: ['wacontents', current.id],
        queryParams: { orgId: this.orgId },
      })
    }
    const chatbot = chatbotFromRecord(record)
    if (!((chatbot.nodes || []).length)) {
      const name = String((current && current.title) || '').trim()
      return name ? `Chatbot "${name}" has no steps` : 'There are no steps'
    }
    const keys = mappedTemplateKeys(chatbot)
    if (!keys.length) return ''
    try {
      const resp = await ApiHelper.callAwait({
        ms: this.ms,
        method: 'POST',
        paths: ['miscs', 'wa-template-preview'],
        queryParams: { orgId: this.orgId },
        jsonBody: { waTemplateKeys: keys },
      })
      return firstTemplateIssue(chatbot, indexWaTemplatePreviews(resp))
        ? invalidStepsReason(current && current.title)
        : ''
    } catch (err) {
      return Helper.getErrorMsg(err) || 'WhatsApp template details could not be loaded'
    }
  }

  onToggleChatbotState = async (current, enabled) => {
    const id = current && current.id
    if (!id) return
    if (enabled === isChatbotLive(current.state)) return
    if (enabled) {
      let reason = ''
      try {
        reason = await this.liveBlockReason(current)
      } catch (err) {
        this.showToast(Helper.getErrorMsg(err), 'error')
        return
      }
      if (reason) {
        this.showToast(reason, 'error')
        return
      }
    }
    const state = enabled ? 'LIVE' : 'DISABLED'
    this.setState(prev => ({
      togglingIds: { ...(prev.togglingIds || {}), [id]: true },
    }))
    try {
      await this.putChatbot(id, {
        title: current.title || '',
        state,
      })
      this.setState(prev => ({
        masterItems: withChatbotState(prev.masterItems, id, state),
        items: withChatbotState(prev.items, id, state),
        togglingIds: { ...(prev.togglingIds || {}), [id]: false },
      }))
      this.showToast(`Chatbot "${current.title}" is now ${state}`, 'success')
    } catch (err) {
      this.setState(prev => ({
        togglingIds: { ...(prev.togglingIds || {}), [id]: false },
      }))
      this.showToast(Helper.getErrorMsg(err), 'error')
    }
  }

  showToast = (message, severity = 'success') => {
    this.setState({
      showToast: true,
      toastMsg: message,
      isErrorToast: severity === 'error',
    })
  }

  async customSubmit(options) {
    const { formData, caller } = options
    if (this.formDefinition && this.formDefinition._subType === 'create-copy') {
      const title = String(formData.title || '').trim()
      if (!title) {
        caller.setState({ inProcess: false, success: false, message: 'Provide chatbot name.' })
        return
      }
      try {
        const response = await ApiHelper.callAwait({
          ms: this.ms,
          method: 'POST',
          paths: ['miscs', 'create-copy'],
          queryParams: { orgId: this.orgId },
          jsonBody: {
            type: 'WACONTENT',
            orgId: this.orgId,
            id: this.apiData && this.apiData.id,
            title,
          },
        })
        const id = recordId(response)
        caller.setState({ inProcess: false, success: true, message: '' })
        this.setState({ modalOpen: false }, () => {
          if (id) this.openChatbot(id)
          else this.fetchItems()
        })
      } catch (err) {
        const errorMessage = Helper.getErrorMsg(err)
        caller.setState({ inProcess: false, success: false, message: errorMessage })
      }
      return
    }
    const title = String(formData.title || '').trim()
    if (!title) {
      caller.setState({
        inProcess: false,
        success: false,
        message: 'Provide chatbot name.',
      })
      return
    }
    if (this.formDefinition && this.formDefinition.formType === 'edit') {
      const id = this.apiData && this.apiData.id
      if (!id) {
        caller.setState({ inProcess: false, success: false, message: 'Chatbot id is missing.' })
        return
      }
      try {
        await this.putChatbot(id, {
          title,
          state: this.apiData.state,
        })
        caller.setState({ inProcess: false, success: true, message: '' })
        this.setState({ modalOpen: false }, () => this.fetchItems())
      } catch (err) {
        caller.setState({
          inProcess: false,
          success: false,
          message: Helper.getErrorMsg(err),
        })
      }
      return
    }
    try {
      const response = await ApiHelper.callAwait({
        ms: this.ms,
        method: 'POST',
        paths: ['wacontents'],
        queryParams: { orgId: this.orgId },
        jsonBody: {
          orgId: this.orgId,
          title,
          iType: 'CHATBOT',
          state: 'DISABLED',
          botJson: botJsonFromChatbot(createEmptyChatbot()),
        },
      })
      const id = recordId(response)
      if (!id) throw new Error('Chatbot was created but id was not returned.')
      caller.setState({ inProcess: false, success: true, message: '' })
      this.setState({ modalOpen: false }, () => this.openChatbot(id))
    } catch (err) {
      caller.setState({
        inProcess: false,
        success: false,
        message: Helper.getErrorMsg(err),
      })
    }
  }

  onSearch = (searchText = '') => {
    this.setState({
      searchText,
      items: filterRows(this.state.masterItems, searchText),
    })
  }

  clearSearch = () => {
    this.setState({
      searchText: '',
      items: filterRows(this.state.masterItems, ''),
    })
  }

  onDrillDown = (current) => {
    this.openChatbot(current && current.id)
  }

  onActions = (current) => {
    return [
      UiHelper.buttonEdit(this, current),
      <Tooltip title='View Chatbot' placement='top-start' key={`${current.id}-drilldown`}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i
            className='material-icons-outlined'
            onClick={() => this.onDrillDown(current)}
          >
            arrow_forward_ios
          </i>
        </span>
      </Tooltip>,
    ]
  }

  onMenuItems = (current) => {
    if (!Controller.get().roleHelper().canEditOrg()) return []
    return [
      UiHelper.actionButton({
        current,
        key: current.id + 'create-copy',
        action: () => UiHelper.openModalForCreateCopy.call(this, current, 'WACONTENT'),
        title: Helper.getString('createcopy'),
        icon: 'copy',
      }),
    ]
  }

  renderAboveTable() {
    return (
      <div className={css(Styles.toolbar)}>
        <SearchBar
          hint='Search by name'
          onSearch={this.onSearch}
          onClearSearch={this.clearSearch}
          searchText={this.state.searchText}
          searchButtonLabel='Filter'
          clearSearchLabel='Clear'
          autoRefresh
          debounceDuration={1}
          autoFocus={false}
          hideFiltersContainer
        />
      </div>
    )
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) return ''
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }
}

const Styles = StyleSheet.create({
  toolbar: {
    width: '100%',
    marginBottom: 16,
  },
  line1: {
    fontSize: 16,
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
  },
})
